import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import localeText from "../../../../../../../locale";
import FormSectionHeader from "../components/FormSectionHeader";
import { createLocalId } from "../utils";

const createDraftItem = () => ({ localId: createLocalId(), key: "", value: "" });
const createDraft = () => ({ name: "", items: [createDraftItem()] });

function CharacteristicsSection({ form }) {
  const isService = form.productType === 2;
  const groups = form.characteristicGroups || [];
  const [draft, setDraft] = useState(createDraft);
  const [expandedIds, setExpandedIds] = useState([]);
  const [isComposing, setIsComposing] = useState(false);
  const showEmptyState = groups.length === 0 && !isComposing;

  const updateDraftName = (name) => setDraft((prev) => ({ ...prev, name }));

  const updateDraftItem = (itemIndex, patch) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.map((item, index) => (index === itemIndex ? { ...item, ...patch } : item)),
    }));
  };

  const addDraftItem = () => {
    setDraft((prev) => ({ ...prev, items: [...prev.items, createDraftItem()] }));
  };

  const removeDraftItem = (itemIndex) => {
    setDraft((prev) => {
      const nextItems = prev.items.filter((_, index) => index !== itemIndex);
      return { ...prev, items: nextItems.length ? nextItems : [createDraftItem()] };
    });
  };

  const completeItems = (draft.items || []).filter(
    (item) => item.key?.trim() && item.value?.trim(),
  );
  const canSave = Boolean(draft.name?.trim()) && completeItems.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    const saved = form.saveCharacteristicGroup({
      name: draft.name.trim(),
      items: completeItems.map((item) => ({
        key: item.key.trim(),
        value: item.value.trim(),
      })),
    });
    /* if (saved?.localId) {
      setExpandedIds((prev) => (prev.includes(saved.localId) ? prev : [...prev, saved.localId]));
    } */
    setDraft(createDraft());
    setIsComposing(true);
  };

  const startComposing = () => {
    setDraft(createDraft());
    setIsComposing(true);
  };

  const handleRemoveGroup = (groupIndex) => {
    const isLastGroup = groups.length === 1;
    form.removeCharacteristicGroup(groupIndex);
    if (isLastGroup) {
      setIsComposing(false);
      setDraft(createDraft());
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const svgIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.625 0V2.625H0V4.375H2.625V7H4.375V4.375H7V2.625H4.375V0H2.625ZM1.75 16.6308V8.75H3.5V15.75H10.5V11.375C10.5 10.8937 10.8937 10.5 11.375 10.5L15.75 10.4991V3.5H8.75V1.75H16.6308C17.1108 1.75 17.5 2.14879 17.5 2.62716V12.25L12.25 17.4965L2.62693 17.5C2.14261 17.5 1.75 17.1107 1.75 16.6308ZM15.0246 12.2491L12.25 12.25V15.0229L15.0246 12.2491Z"
        fill="var(--theme-secondary)"
      />
    </svg>
  );

  return (
    <section className="product-form-card flex flex-column gap-4">
      <FormSectionHeader
        icon={svgIcon}
        title={localeText(isService ? "serviceCharacteristics" : "productCharacteristics")}
        subtitle={localeText(
          isService ? "serviceCharacteristicsSubtitle" : "productCharacteristicsSubtitle",
        )}
      />

      {showEmptyState ? (
        <div className="product-form-empty">
          <p className="m-0 mb-2 font-medium">{localeText("noCharacteristics")}</p>
          <button type="button" className="product-form-link underline text-sm font-medium" onClick={startComposing}>
            {localeText(isService ? "createCharacteristicsService" : "createCharacteristics")}
          </button>
        </div>
      ) : (
        <>
          {groups.length > 0 && (
            <div className="product-form-characteristic-list">
              {groups.map((group, groupIndex) => {
                const itemCount = (group.items || []).length;
                const isOpen = expandedIds.includes(group.localId);
                const groupName = group.name?.trim() || localeText("unnamedCharacteristicGroup");
                const countLabel = localeText(
                  itemCount === 1 ? "characteristicGroupItem" : "characteristicGroupItems",
                  { count: itemCount },
                );

                return (
                  <div key={group.localId} className="product-form-characteristic-card">
                    <Button
                      type="button"
                      className="product-form-characteristic-card-header p-no-outline"
                      onClick={() => toggleGroup(group.localId)}
                      aria-expanded={isOpen}
                    >
                      <div className="product-form-characteristic-card-title flex items-center text-sm font-bold">
                        <span className="mr-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 20 20"
                            fill="none"
                            style={{ color: "var(--theme-text-muted)" }}
                          >
                            <path
                              d="M6.5 15.25C7.46421 15.25 8.25 16.0358 8.25 17C8.25 17.9642 7.46421 18.75 6.5 18.75C5.53579 18.75 4.75 17.9642 4.75 17C4.75 16.0358 5.53579 15.25 6.5 15.25ZM13.5 15.25C14.4642 15.25 15.25 16.0358 15.25 17C15.25 17.9642 14.4642 18.75 13.5 18.75C12.5358 18.75 11.75 17.9642 11.75 17C11.75 16.0358 12.5358 15.25 13.5 15.25ZM6.5 8.25C7.46421 8.25 8.25 9.03579 8.25 10C8.25 10.9642 7.46421 11.75 6.5 11.75C5.53579 11.75 4.75 10.9642 4.75 10C4.75 9.03579 5.53579 8.25 6.5 8.25ZM13.5 8.25C14.4642 8.25 15.25 9.03579 15.25 10C15.25 10.9642 14.4642 11.75 13.5 11.75C12.5358 11.75 11.75 10.9642 11.75 10C11.75 9.03579 12.5358 8.25 13.5 8.25ZM6.5 1.25C7.46421 1.25 8.25 2.03579 8.25 3C8.25 3.96421 7.46421 4.75 6.5 4.75C5.53579 4.75 4.75 3.96421 4.75 3C4.75 2.03579 5.53579 1.25 6.5 1.25ZM13.5 1.25C14.4642 1.25 15.25 2.03579 15.25 3C15.25 3.96421 14.4642 4.75 13.5 4.75C12.5358 4.75 11.75 3.96421 11.75 3C11.75 2.03579 12.5358 1.25 13.5 1.25Z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                        {groupName}{" "}
                        <span className="ml-1 font-normal font-italic">({countLabel})</span>
                      </div>
                      <i
                        className={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"} text-sm`}
                        style={{ color: "var(--theme-text-muted)" }}
                      />
                    </Button>

                    {isOpen && (
                      <div className="product-form-characteristic-card-body">
                        {(group.items || []).map((item) => (
                          <div
                            key={item.localId}
                            className="product-form-characteristic-saved-item text-xs"
                          >
                            <div>
                              <p className="product-form-characteristic-saved-value font-semibold">
                                {item.key}
                              </p>
                            </div>
                            <div>
                              <p className="product-form-characteristic-saved-value">
                                {item.value}
                              </p>
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          label={localeText("removeCharacteristicGroup")}
                          className="p-button-text p-button-danger product-form-characteristic-remove"
                          onClick={() => handleRemoveGroup(groupIndex)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div
            className="product-form-characteristic-composer flex flex-column"
            style={{ gap: "0.75rem" }}
          >
            <div className="product-form-characteristic-name-row">
              <InputText
                value={draft.name}
                onChange={(e) => updateDraftName(e.target.value)}
                placeholder={localeText("groupNamePlaceholder")}
                className="w-full border-round-lg"
              />
              <Button
                type="button"
                icon="pi pi-check"
                label={localeText("saveCharacteristicGroup")}
                className="product-form-characteristic-save border-round-lg"
                disabled={!canSave}
                onClick={handleSave}
              />
            </div>
            <div>
              <div className="product-form-characteristic-item-head text-sm">
                <span>{localeText("characteristicTitle")}</span>
                <span>{localeText("characteristicDescription")}</span>
              </div>

              {(draft.items || []).map((item, itemIndex) => (
                <div key={item.localId} className="product-form-characteristic-item-row">
                  <InputText
                    value={item.key}
                    onChange={(e) => updateDraftItem(itemIndex, { key: e.target.value })}
                    placeholder={localeText("characteristicTitlePlaceholder")}
                    className="w-full border-round-lg"
                  />
                  <InputText
                    value={item.value}
                    onChange={(e) => updateDraftItem(itemIndex, { value: e.target.value })}
                    placeholder={localeText("characteristicDescriptionPlaceholder")}
                    className="w-full border-round-lg"
                  />
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-text p-button-rounded p-button-danger"
                    onClick={() => removeDraftItem(itemIndex)}
                    disabled={draft.items.length === 1 && !item.key && !item.value}
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              className="product-form-link text-sm font-medium flex align-items-center"
              onClick={addDraftItem}
            >
              <i className="pi pi-plus mr-1 text-xs font-semibold" /> {localeText("addItem")}
            </button>
          </div>
        </>
      )}

      <p className="product-form-help">
        {localeText(isService ? "characteristicsFooterService" : "characteristicsFooter")}
      </p>
    </section>
  );
}

export default CharacteristicsSection;
